/**
 * The source editor (web.md §4 Sources; the prototype's Sources form, design fix 3): Source (name, type,
 * pull frequency, the kind's own address fields, description), Content (text & formatting, pictures,
 * videos), History (usefulness, kept, received, last pull, last error) and Test source / Harvest now.
 *
 * New sources: type + address → `sources.resolve` (the "Look up" preview) → `sources.create`. Kind and
 * address are the source's identity and cannot change afterwards. Secrets (mail password, Mastodon
 * token, GitHub token) are held in component state only and handed to the engine as `secrets`.
 */

import { useEffect, useRef, useState } from 'react';
import {
  DigesterError,
  toDigesterError,
  type SecretKey,
  type Source,
  type SourceConfig,
  type SourceFormat,
  type SourceInput,
  type SourceKind,
  type SourceResolution,
  type SourceTestResult,
  type TelegramDialog,
} from '@digester/core';
import { reportError } from '../../app/errors.js';
import { showToast } from '../../app/Toast.js';
import { useEngine, useEngineQuery } from '../../engine-client/EngineProvider.js';
import { qk } from '../../engine-client/keys.js';
import { Icon } from '../../icons/Icon.js';
import { formatCount } from '../../ui/format.js';
import { Field, FormGrid, Section, Select, TextArea, Toggle } from '../../ui/form.js';
import { Tool } from '../../ui/Tool.js';
import { SOURCE_KIND_LABELS, sourceKindIcon } from '../../ui/source-icons.js';
import { SOURCE_KINDS_ORDER } from '../rules/model.js';
import { EditorFrame, SaveIslandTools, useDraft } from '../shell/index.js';
import { EmailFields, type EmailConfigDraft } from './EmailFields.js';
import { describeSourceError, describeStatusError } from './errors.js';
import { DEFAULT_PULL, FORMAT_OPTIONS, KINDS, PULL_OPTIONS, WATCH_PLATFORMS } from './kinds.js';
import { TelegramAccount } from './TelegramAccount.js';
import { WatcherPanel } from './WatcherPanel.js';

/** Form values. `config` holds the kind's raw form fields (strings, numbers, booleans), converted on save. */
export interface SourceDraft {
  kind: SourceKind;
  name: string;
  address: string;
  config: Record<string, string | number | boolean>;
  pullMinutes: number;
  format: SourceFormat;
  keepImages: boolean;
  keepVideos: boolean;
  description: string;
  enabled: boolean;
  /** Items taken on the first harvest of a new source; null = the engine's default (sources.defaults()). */
  backfillItems: number | null;
}

/** "First harvest" choices for a new source (the engine's default, ingest.new_source_backfill_items, is added when it is not one of them). */
export const BACKFILL_STEPS: readonly number[] = [5, 10, 30];

interface Secrets {
  password: string;
  token: string;
  githubToken: string;
}

const NO_SECRETS: Secrets = { password: '', token: '', githubToken: '' };

export function newSourceDraft(kind: SourceKind = 'rss'): SourceDraft {
  return {
    kind,
    name: '',
    address: '',
    config: { ...(KINDS[kind].config as Record<string, string | number | boolean>), ...(kind === 'email' ? { host: '', user: '', fromFilter: '' } : {}), ...(kind === 'youtube' ? { languages: '' } : {}) },
    pullMinutes: DEFAULT_PULL[kind],
    format: 'original',
    keepImages: true,
    keepVideos: true,
    description: '',
    enabled: true,
    backfillItems: null,
  };
}

/** Form values of an existing source (or of the source a duplicate copies). */
export function sourceDraftOf(s: Source): SourceDraft {
  const c = s.config as unknown as Record<string, unknown>;
  const config: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(c)) {
    if (k === 'kind') continue;
    if (Array.isArray(v)) config[k] = v.join(', ');
    else if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') config[k] = v;
  }
  if (s.kind === 'email' && config.fromFilter === undefined) config.fromFilter = '';
  if (s.kind === 'youtube' && config.languages === undefined) config.languages = '';
  return {
    kind: s.kind,
    name: s.name,
    address: addressOf(s),
    config,
    pullMinutes: s.pullMinutes,
    format: s.format,
    keepImages: s.keepImages,
    keepVideos: s.keepVideos,
    description: s.description,
    enabled: s.enabled,
    backfillItems: s.backfillItems,
  };
}

function addressOf(s: Source): string {
  const c = s.config as unknown as Record<string, unknown>;
  if (s.kind === 'rss') return String(c.feedUrl ?? s.locator);
  if (s.kind === 'telegram') return c.username ? `@${String(c.username)}` : s.locator;
  if (s.kind === 'github') return `${String(c.owner)}/${String(c.repo)}`;
  if (s.kind === 'watcher') return String(c.target ?? '');
  if (s.kind === 'reddit') return `${c.mode === 'user' ? 'u' : 'r'}/${String(c.name)}`;
  if (s.kind === 'discourse') return String(c.baseUrl ?? s.locator);
  return s.locator;
}

const list = (text: unknown): string[] =>
  String(text ?? '')
    .split(',')
    .map((x) => x.trim())
    .filter((x) => x !== '');

/** Form config → the config `sources.resolve` / `sources.update` understands. */
function configOf(kind: SourceKind, raw: Record<string, string | number | boolean>, onlyMutable: boolean): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (onlyMutable && !KINDS[kind].mutable.includes(k)) continue;
    if (k === 'fromFilter' || k === 'languages') out[k] = list(v);
    else if (k === 'port') out[k] = Number(v);
    else if (typeof v === 'string' && v.trim() === '' && k !== 'fromFilter') continue;
    else out[k] = typeof v === 'string' ? v.trim() : v;
  }
  return out;
}

export function resolveInputOf(d: SourceDraft): SourceInput {
  const config = configOf(d.kind, d.config, false);
  const address = d.kind === 'email' ? String(d.config.user ?? '') : d.kind === 'telegram' && d.config.mode === 'account' ? String(d.config.peerId ?? '') : d.address.trim();
  return { kind: d.kind, address, config: config as Partial<SourceConfig> } as SourceInput;
}

function secretsFor(kind: SourceKind, resolution: Pick<SourceResolution, 'requiredSecrets'> | null, id: string | null, s: Secrets): Partial<Record<SecretKey, string>> {
  const out: Partial<Record<SecretKey, string>> = {};
  const own = (suffix: 'password' | 'token'): SecretKey | undefined =>
    resolution?.requiredSecrets.find((k) => k.endsWith(`:${suffix}`)) ?? (id ? (`source:${id}:${suffix}` as SecretKey) : undefined);
  if (kind === 'email' && s.password !== '') {
    const key = own('password');
    if (key) out[key] = s.password;
  }
  if (kind === 'mastodon' && s.token !== '') {
    const key = own('token');
    if (key) out[key] = s.token;
  }
  if (kind === 'github' && s.githubToken.trim() !== '') out['github:token'] = s.githubToken.trim();
  return out;
}

function when(ms: number | null): string {
  if (ms === null) return 'Not yet';
  const d = new Date(ms);
  const today = new Date();
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return d.toDateString() === today.toDateString() ? time : `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${time}`;
}

/**
 * "First harvest" (new sources only): how many of the newest items each device takes the first time it pulls
 * the source. The engine's default is a config row (ingest.new_source_backfill_items); later pulls take
 * everything new up to ingest.max_items_per_fetch.
 */
function FirstHarvest({ value, defaults, onChange }: { value: number | null; defaults: { backfillItems: number; backfillMax: number } | undefined; onChange(n: number | null): void }) {
  const fallback = defaults?.backfillItems ?? BACKFILL_STEPS[1] ?? 10;
  const max = defaults?.backfillMax ?? Infinity;
  const steps = [...new Set([...BACKFILL_STEPS, fallback])].filter((n) => n <= max).sort((a, b) => a - b);
  const current = value ?? fallback;
  return (
    <>
      <Select
        label="First harvest"
        name="backfill"
        value={String(current)}
        options={steps.map((n) => [String(n), `Newest ${n} messages${n === fallback ? ' (default)' : ''}`] as const)}
        onChange={(n) => onChange(Number(n) === fallback ? null : Number(n))}
      />
      <p className="field-help">How many of its latest messages arrive the first time the source is pulled. After that, only new ones.</p>
    </>
  );
}

type Probe = { kind: 'idle' } | { kind: 'busy'; what: string } | { kind: 'test'; result: SourceTestResult } | { kind: 'error'; message: string } | { kind: 'harvest'; text: string };

export function SourceForm({ source, initial }: { source: Source | null; initial: SourceDraft }) {
  const engine = useEngine();
  const isNew = source === null;
  const secretsRef = useRef<Secrets>(NO_SECRETS);
  const [secrets, setSecretsState] = useState<Secrets>(NO_SECRETS);
  const [preview, setPreview] = useState<{ input: string; resolution: SourceResolution } | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);
  const [probe, setProbe] = useState<Probe>({ kind: 'idle' });
  const availability = useEngineQuery(['sources', 'availability'], (e) => e.sources.availability()).data;
  const backfill = useEngineQuery(['sources', 'defaults'], (e) => e.sources.defaults(), { enabled: isNew }).data;

  const resolve = async (d: SourceDraft): Promise<SourceResolution> => {
    const input = resolveInputOf(d);
    const key = JSON.stringify(input);
    if (preview && preview.input === key) return preview.resolution;
    setLooking(true);
    setLookupError(null);
    try {
      const resolution = await engine.sources.resolve(input);
      setPreview({ input: key, resolution });
      return resolution;
    } catch (error) {
      const human = describeSourceError(error, d.kind === 'email' ? 'imap' : 'relay');
      setLookupError(human);
      const e = toDigesterError(error);
      throw new DigesterError(e.code, human, e.details);
    } finally {
      setLooking(false);
    }
  };

  const draft = useDraft<SourceDraft>({
    initial,
    isNew,
    onSave: async (v) => {
      const s = secretsRef.current;
      if (source) {
        const secretsPatch = secretsFor(v.kind, null, source.id, s);
        const mutable = configOf(v.kind, v.config, true);
        await engine.sources.update(source.id, {
          name: v.name.trim(),
          pullMinutes: v.pullMinutes,
          format: v.format,
          keepImages: v.keepImages,
          keepVideos: v.keepVideos,
          description: v.description.trim(),
          enabled: v.enabled,
          ...(Object.keys(mutable).length ? { config: mutable } : {}),
          ...(Object.keys(secretsPatch).length ? { secrets: secretsPatch } : {}),
        });
        setSecrets(NO_SECRETS, false);
        return source.id;
      }
      const resolution = await resolve(v);
      const secretsInput = secretsFor(v.kind, resolution, null, s);
      const created = await engine.sources.create({
        resolution,
        name: v.name.trim() || resolution.name,
        pullMinutes: v.pullMinutes,
        format: v.format,
        keepImages: v.keepImages,
        keepVideos: v.keepVideos,
        description: v.description.trim(),
        ...(v.backfillItems !== null ? { backfillItems: Math.min(v.backfillItems, backfill?.backfillMax ?? v.backfillItems) } : {}),
        ...(Object.keys(secretsInput).length ? { secrets: secretsInput } : {}),
      });
      if (!v.enabled) await engine.sources.update(created.id, { enabled: false });
      setSecrets(NO_SECRETS, false);
      return created.id;
    },
  });
  const v = draft.value;

  function setSecrets(next: Secrets, dirty = true): void {
    secretsRef.current = next;
    setSecretsState(next);
    if (dirty) draft.set((d) => d);
  }
  const setConfig = (patch: Record<string, string | number | boolean>): void => draft.set((d) => ({ ...d, config: { ...d.config, ...patch } }));

  // A changed address invalidates the preview.
  const inputKey = JSON.stringify(resolveInputOf(v));
  useEffect(() => {
    if (preview && preview.input !== inputKey) setPreview(null);
    setLookupError(null);
  }, [inputKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const status = useEngineQuery(qk.sourceStatus(), (e) => e.sources.status(), { enabled: !isNew }).data?.find((x) => x.sourceId === source?.id);
  const stats = useEngineQuery(qk.sourceStats(source?.id), (e) => (source ? e.sources.stats(source.id) : Promise.resolve(null)), { enabled: !isNew }).data;
  const kindAvailability = availability?.find((a) => a.kind === v.kind);
  const telegramAccountAvailable = v.kind !== 'telegram' || !(kindAvailability?.reason ?? '').startsWith('Public channels only');

  const lookUp = async (): Promise<void> => {
    const form = document.getElementById('settings-form');
    if (form instanceof HTMLFormElement && !form.reportValidity()) return;
    try {
      const r = await resolve(v);
      if (v.name.trim() === '') draft.set((d) => ({ ...d, name: r.name }));
    } catch {
      /* shown inline */
    }
  };

  const testSource = async (): Promise<void> => {
    if (!source) return;
    setProbe({ kind: 'busy', what: 'test' });
    try {
      setProbe({ kind: 'test', result: await engine.sources.test(source.id) });
    } catch (error) {
      setProbe({ kind: 'error', message: describeSourceError(error) });
    }
  };

  const harvestNow = async (): Promise<void> => {
    if (!source) return;
    setProbe({ kind: 'busy', what: 'harvest' });
    try {
      const report = await engine.sources.harvestNow(source.id);
      const failed = report.failed[0];
      if (failed) setProbe({ kind: 'error', message: describeStatusError(failed) });
      else {
        const text = `Harvested: ${formatCount(report.inserted)} new, ${formatCount(report.duplicates)} already known${report.overLimit ? `, ${formatCount(report.overLimit)} over today’s limit` : ''}.`;
        setProbe({ kind: 'harvest', text });
        showToast(report.inserted ? `${report.inserted} new message${report.inserted === 1 ? '' : 's'} from ${source.name}` : 'No new messages');
      }
    } catch (error) {
      setProbe({ kind: 'error', message: describeSourceError(error) });
      if (toDigesterError(error).code === 'plan_limit') reportError(error);
    }
  };

  const testExistingEmail = async (): Promise<{ ok: boolean; message: string }> => {
    if (!source) return { ok: false, message: 'Save the source first.' };
    const r = await engine.sources.test(source.id);
    return r.ok ? { ok: true, message: `Connected. ${r.sample.length} recent message${r.sample.length === 1 ? '' : 's'} found.` } : { ok: false, message: r.error ? describeStatusError(r.error) : 'The test failed.' };
  };

  const pick = (d: TelegramDialog): void =>
    draft.set((x) => ({ ...x, name: x.name.trim() === '' || x.name === previousDialogName.current ? d.title : x.name, config: { ...x.config, mode: 'account', peerId: d.peerId, peerType: d.peerType } }));
  const previousDialogName = useRef('');

  const emailValue: EmailConfigDraft = {
    host: String(v.config.host ?? ''),
    port: Number(v.config.port ?? 993),
    security: (v.config.security as EmailConfigDraft['security']) ?? 'tls',
    user: String(v.config.user ?? ''),
    mailbox: String(v.config.mailbox ?? 'INBOX'),
    fromFilter: String(v.config.fromFilter ?? ''),
  };

  const failing = status?.lastError ?? null;
  const headerStatus = !isNew && source && !source.enabled && source.disabledReason === 'plan' ? { label: 'Paused by plan', on: false } : undefined;

  return (
    <EditorFrame
      title={v.name.trim() || source?.name || 'New source'}
      icon={sourceKindIcon(v.kind)}
      enabled={{ checked: v.enabled, onChange: (enabled) => draft.set((d) => ({ ...d, enabled })) }}
      {...(headerStatus ? { status: headerStatus } : {})}
    >
      {source ? (
        <SaveIslandTools>
          <Tool icon="play" label="Test source" onClick={() => void testSource()} />
          {v.kind !== 'watcher' ? <Tool icon="refresh-cw" label="Harvest now" onClick={() => void harvestNow()} /> : null}
        </SaveIslandTools>
      ) : null}
      <Section title="Source">
        <Field label="Name" name="name" value={v.name} required={!isNew} maxLength={200} placeholder={isNew ? 'From the source when left empty' : undefined} onChange={(name) => draft.set((d) => ({ ...d, name }))} />
        <FormGrid>
          <Select
            label="Type"
            name="type"
            value={v.kind}
            disabled={!isNew}
            options={SOURCE_KINDS_ORDER.map((k) => [k, SOURCE_KIND_LABELS[k]] as const)}
            onChange={(kind) => {
              setPreview(null);
              setSecrets(NO_SECRETS, false);
              draft.set((d) => ({ ...newSourceDraft(kind), name: d.name, description: d.description, enabled: d.enabled, backfillItems: d.backfillItems }));
            }}
          />
          <Select
            label="Pull frequency"
            name="frequency"
            value={String(v.pullMinutes)}
            disabled={v.kind === 'watcher'}
            options={
              v.kind === 'watcher'
                ? [['0', 'Push only'] as const]
                : [
                    ...(v.pullMinutes === 0 ? [['0', 'Never (push only)'] as const] : []),
                    ...PULL_OPTIONS.map(([m, t]) => [String(m), t] as const),
                    ...(v.pullMinutes === 0 || PULL_OPTIONS.some(([m]) => m === v.pullMinutes) ? [] : [[String(v.pullMinutes), `Every ${v.pullMinutes} minutes`] as const]),
                  ]
            }
            onChange={(m) => draft.set((d) => ({ ...d, pullMinutes: Number(m) }))}
          />
        </FormGrid>
        {kindAvailability && (!kindAvailability.available || kindAvailability.reason) && v.kind !== 'watcher' ? (
          <div className="editor-note source-availability" data-availability={v.kind}>
            {kindAvailability.reason ?? 'Not available in this browser.'}
          </div>
        ) : null}

        {v.kind === 'email' ? (
          <EmailFields
            value={emailValue}
            existing={!isNew}
            password={secrets.password}
            onPassword={(password) => setSecrets({ ...secretsRef.current, password })}
            onChange={(e) => setConfig({ host: e.host, port: e.port, security: e.security, user: e.user, mailbox: e.mailbox, fromFilter: e.fromFilter })}
            onTestExisting={testExistingEmail}
          />
        ) : (
          <KindFields
            draft={v}
            isNew={isNew}
            setAddress={(address) => draft.set((d) => ({ ...d, address }))}
            setConfig={setConfig}
            secrets={secrets}
            setSecret={(patch) => setSecrets({ ...secretsRef.current, ...patch })}
            telegramAccountAvailable={telegramAccountAvailable}
            onPickChat={(d) => {
              previousDialogName.current = d.title;
              pick(d);
            }}
          />
        )}

        {isNew && v.kind !== 'email' && !(v.kind === 'telegram' && v.config.mode === 'account') ? (
          <div className="source-lookup">
            <button type="button" className="secondary-button" data-action="look-up" disabled={looking} onClick={() => void lookUp()}>
              <Icon name="search" /> {looking ? 'Looking up…' : 'Look up'}
            </button>
            <span>Checks the address and shows what will be added.</span>
          </div>
        ) : null}
        {lookupError ? (
          <div className="test-error" role="alert" data-lookup-error>
            <Icon name="circle-alert" />
            <span>{lookupError}</span>
          </div>
        ) : null}
        {preview && isNew ? (
          <div className="source-preview" data-preview>
            <span className="setting-item-icon">
              <Icon name={sourceKindIcon(preview.resolution.kind)} />
            </span>
            <span>
              <strong>{preview.resolution.name}</strong>
              <small>{preview.resolution.locator}</small>
            </span>
          </div>
        ) : null}

        {isNew && v.kind !== 'watcher' ? <FirstHarvest value={v.backfillItems} defaults={backfill} onChange={(backfillItems) => draft.set((d) => ({ ...d, backfillItems }))} /> : null}

        <TextArea label="Description" name="description" value={v.description} onChange={(description) => draft.set((d) => ({ ...d, description }))} />
      </Section>

      {source?.kind === 'watcher' ? <WatcherPanel source={source} /> : null}

      <Section title="Content">
        <Select label="Text & formatting" name="format" value={v.format} options={FORMAT_OPTIONS} onChange={(format) => draft.set((d) => ({ ...d, format }))} />
        <Toggle label="Include pictures" name="images" checked={v.keepImages} onChange={(keepImages) => draft.set((d) => ({ ...d, keepImages }))} />
        <Toggle label="Include videos" name="videos" checked={v.keepVideos} onChange={(keepVideos) => draft.set((d) => ({ ...d, keepVideos }))} />
      </Section>

      {source ? (
        <Section title="History">
          <div className="stat-grid">
            <div>
              <span>Usefulness</span>
              <strong>{stats?.usefulness == null ? '—' : stats.usefulness.toFixed(3)}</strong>
            </div>
            <div>
              <span>Kept</span>
              <strong title={stats ? String(stats.kept) : undefined}>{stats ? formatCount(stats.kept) : '—'}</strong>
            </div>
            <div>
              <span>Received</span>
              <strong title={stats ? String(stats.received) : undefined}>{stats ? formatCount(stats.received) : '—'}</strong>
            </div>
          </div>
          <p className="field-help">Based on what you keep, not factual accuracy. Source statistics are preserved when messages are deleted.</p>
          <div className="inline-meta">
            Last pull <strong>{source.kind === 'watcher' ? 'Push only' : when(status?.lastOkAt ?? null)}</strong>
          </div>
          {status?.needsSecret ? (
            <div className="inline-meta source-problem" data-source-problem="secret">
              Needs <strong>{source.kind === 'email' ? 'the mailbox password on this device' : 'its token on this device'}</strong>
            </div>
          ) : null}
          {failing ? (
            <div className="inline-meta source-problem" data-source-problem="error">
              Last error <strong>{describeStatusError(failing)}</strong>
            </div>
          ) : null}
        </Section>
      ) : null}

      {source ? (
        <>
          <div className="rule-test source-actions">
            <button className="secondary-button" type="button" data-action="test-source" disabled={probe.kind === 'busy'} onClick={() => void testSource()}>
              <Icon name="play" /> Test source
            </button>
            {source.kind !== 'watcher' ? (
              <button className="secondary-button" type="button" data-action="harvest-now" disabled={probe.kind === 'busy'} onClick={() => void harvestNow()}>
                <Icon name="refresh-cw" /> Harvest now
              </button>
            ) : null}
            <span>{probe.kind === 'busy' ? (probe.what === 'test' ? 'Fetching…' : 'Harvesting…') : 'Test fetches without saving anything'}</span>
          </div>
          <div id="source-test-results" aria-live="polite">
            {probe.kind === 'test' ? (
              probe.result.ok ? (
                <>
                  <div className="test-summary">
                    {probe.result.sample.length ? `${probe.result.sample.length} item${probe.result.sample.length === 1 ? '' : 's'} available now` : 'Reachable, no items right now'}
                    <span>{probe.result.via ? `via ${probe.result.via} · ` : ''}nothing was saved</span>
                  </div>
                  {probe.result.sample.map((item, i) => (
                    <div className="test-row" key={i} data-result="match">
                      <Icon name="check" />
                      <span>{item.title || '(no title)'}</span>
                      <b>{item.publishedAt ? when(item.publishedAt) : ''}</b>
                    </div>
                  ))}
                </>
              ) : (
                <div className="test-error" role="alert">
                  <Icon name="circle-alert" />
                  <span>{probe.result.error ? describeStatusError(probe.result.error) : 'The test failed.'}</span>
                </div>
              )
            ) : probe.kind === 'error' ? (
              <div className="test-error" role="alert">
                <Icon name="circle-alert" />
                <span>{probe.message}</span>
              </div>
            ) : probe.kind === 'harvest' ? (
              <div className="test-summary">{probe.text}</div>
            ) : null}
          </div>
        </>
      ) : null}
    </EditorFrame>
  );
}

interface KindFieldsProps {
  draft: SourceDraft;
  isNew: boolean;
  setAddress(address: string): void;
  setConfig(patch: Record<string, string | number | boolean>): void;
  secrets: Secrets;
  setSecret(patch: Partial<Secrets>): void;
  telegramAccountAvailable: boolean;
  onPickChat(dialog: TelegramDialog): void;
}

/** The address and the kind's own fields (everything but email, which has EmailFields). */
function KindFields({ draft: v, isNew, setAddress, setConfig, secrets, setSecret, telegramAccountAvailable, onPickChat }: KindFieldsProps) {
  const info = KINDS[v.kind];
  const c = v.config;
  const address = (label = info.addressLabel ?? 'Address', placeholder = info.placeholder, help = info.help) => (
    <Field
      label={label}
      name="url"
      value={v.address}
      required
      readOnly={!isNew}
      placeholder={placeholder}
      autoComplete="off"
      onChange={setAddress}
      help={isNew ? help : 'The address identifies this source. To follow another one, add a new source.'}
    />
  );
  switch (v.kind) {
    case 'telegram': {
      const mode = String(c.mode ?? 'public');
      return (
        <>
          <Select
            label="Mode"
            name="telegram-mode"
            value={mode}
            disabled={!isNew}
            options={[['public', 'Public channel (no account)'], ['account', telegramAccountAvailable ? 'My Telegram account' : 'My Telegram account · unavailable here']] as const}
            onChange={(m) => setConfig({ mode: m })}
          />
          {mode === 'public' ? address() : null}
          {mode === 'account' && !isNew ? address('Chat') : null}
          {mode === 'account' && isNew ? (
            telegramAccountAvailable ? (
              <TelegramAccount peerId={String(c.peerId ?? '')} onPick={onPickChat} />
            ) : (
              <div className="editor-note" data-telegram-unavailable>
                Your own channels, groups and chats need a Telegram API id, which this build does not have. Only public channels work here: choose “Public channel” and enter
                @name or t.me/name.
              </div>
            )
          ) : null}
        </>
      );
    }
    case 'mastodon': {
      const mode = String(c.mode ?? 'account');
      return (
        <>
          <Select
            label="Follow"
            name="mastodon-mode"
            value={mode}
            disabled={!isNew}
            options={[['account', 'An account’s posts'], ['hashtag', 'A hashtag'], ['home', 'My home timeline']] as const}
            onChange={(m) => setConfig({ mode: m })}
          />
          {mode === 'account' ? address() : null}
          {mode === 'hashtag' ? (
            <FormGrid>
              <Field label="Hashtag" name="url" required readOnly={!isNew} value={v.address} placeholder="#localfirst" onChange={setAddress} />
              <Field label="Instance" name="instance" required readOnly={!isNew} value={String(c.instance ?? '')} placeholder="mastodon.social" onChange={(instance) => setConfig({ instance })} />
            </FormGrid>
          ) : null}
          {mode === 'home' ? (
            <>
              {address('Your account', '@you@mastodon.social')}
              <Field
                label={isNew ? 'Access token' : 'Access token · replace'}
                name="token"
                type="password"
                autoComplete="new-password"
                required={isNew}
                value={secrets.token}
                placeholder={isNew ? 'From Preferences → Development on your instance' : 'Leave empty to keep the token on this device'}
                onChange={(token) => setSecret({ token })}
                help="Stored encrypted on this device only, and sent only to your instance."
              />
            </>
          ) : null}
          <Toggle label="Include replies" name="replies" checked={c.includeReplies === true} onChange={(on) => setConfig({ includeReplies: on })} />
          <Toggle label="Include boosts" name="boosts" checked={c.includeBoosts !== false} onChange={(on) => setConfig({ includeBoosts: on })} />
        </>
      );
    }
    case 'youtube':
      return (
        <>
          {address()}
          <Field label="Caption languages · optional" name="languages" value={String(c.languages ?? '')} placeholder="en, ru" onChange={(languages) => setConfig({ languages })} help="In order of preference. Empty: the video’s own language." />
          <Toggle label="Skip Shorts" name="shorts" checked={c.skipShorts !== false} onChange={(on) => setConfig({ skipShorts: on })} />
        </>
      );
    case 'github':
      return (
        <>
          {address()}
          <Toggle label="New issues" name="issues" checked={c.issues !== false} onChange={(on) => setConfig({ issues: on })} />
          <Toggle label="New comments" name="comments" checked={c.comments !== false} onChange={(on) => setConfig({ comments: on })} />
          <Toggle label="Pull requests" name="pulls" checked={c.pullRequests === true} onChange={(on) => setConfig({ pullRequests: on })} />
          <Field
            label="GitHub token · optional, for every GitHub source on this device"
            name="github-token"
            type="password"
            autoComplete="new-password"
            value={secrets.githubToken}
            placeholder="Leave empty to keep the current one"
            onChange={(githubToken) => setSecret({ githubToken })}
          />
        </>
      );
    case 'bluesky':
      return (
        <>
          {address()}
          <Toggle label="Include replies" name="replies" checked={c.includeReplies === true} onChange={(on) => setConfig({ includeReplies: on })} />
          <Toggle label="Include reposts" name="reposts" checked={c.includeReposts === true} onChange={(on) => setConfig({ includeReposts: on })} />
        </>
      );
    case 'reddit':
      return (
        <>
          {address()}
          <Select
            label="Listing"
            name="listing"
            value={String(c.listing ?? 'new')}
            disabled={!isNew}
            options={[['new', 'New'], ['hot', 'Hot'], ['top', 'Top']] as const}
            onChange={(listing) => setConfig({ listing })}
          />
        </>
      );
    case 'watcher':
      return (
        <>
          <Select label="Platform" name="platform" value={String(c.platform ?? 'webhook')} disabled={!isNew} options={WATCH_PLATFORMS} onChange={(platform) => setConfig({ platform })} />
          {address()}
        </>
      );
    default:
      return address();
  }
}
